// Utility to parse CLOUDINARY_URL format and extract individual components
// Supports both CLOUDINARY_URL format and individual environment variables

export interface CloudinaryConfig {
  cloudName: string;
  apiKey: string;
  apiSecret?: string;
  uploadPreset?: string;
}

/**
 * Parses CLOUDINARY_URL format: cloudinary://<api_key>:<api_secret>@<cloud_name>
 * and extracts individual components
 */
export function parseCloudinaryUrl(cloudinaryUrl: string): Partial<CloudinaryConfig> {
  try {
    // Remove the cloudinary:// protocol
    const urlWithoutProtocol = cloudinaryUrl.replace('cloudinary://', '');
    
    // Split by @ to separate credentials from cloud name
    const [credentials, cloudName] = urlWithoutProtocol.split('@');
    
    if (!cloudName) {
      throw new Error('Invalid CLOUDINARY_URL format: missing cloud name');
    }
    
    // Split credentials by : to get api_key and api_secret
    const [apiKey, apiSecret] = credentials.split(':');
    
    if (!apiKey) {
      throw new Error('Invalid CLOUDINARY_URL format: missing API key');
    }
    
    return {
      cloudName: cloudName.trim(),
      apiKey: apiKey.trim(),
      apiSecret: apiSecret?.trim()
    };
  } catch (error) {
    console.error('Error parsing CLOUDINARY_URL:', error);
    return {};
  }
}

/**
 * Gets Cloudinary configuration from environment variables
 * Supports both CLOUDINARY_URL format and individual variables
 */
export function getCloudinaryConfig(): CloudinaryConfig {
  // Start with individual environment variables
  let cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
  let apiKey = import.meta.env.VITE_CLOUDINARY_API_KEY;
  let apiSecret = import.meta.env.VITE_CLOUDINARY_API_SECRET;
  let uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;
  
  // Always try to parse CLOUDINARY_URL if it exists (prioritize URL values)
  const cloudinaryUrl = import.meta.env.VITE_CLOUDINARY_URL;
  if (cloudinaryUrl) {
    const parsed = parseCloudinaryUrl(cloudinaryUrl);
    
    // Prioritize URL-parsed values over individual variables
    cloudName = parsed.cloudName || cloudName || '';
    apiKey = parsed.apiKey || apiKey || '';
    apiSecret = parsed.apiSecret || apiSecret || '';
  }
  
  // Validate required fields
  if (!cloudName || cloudName === 'your_cloud_name' || cloudName === 'your_cloud_name_here' || cloudName.trim() === '') {
    throw new Error('VITE_CLOUDINARY_CLOUD_NAME is required');
  }
  
  // Clean up cloud name (remove extra spaces)
  cloudName = cloudName.trim();
  
  if (!apiKey || apiKey === 'your_api_key' || apiKey === 'your_api_key_here') {
    throw new Error('VITE_CLOUDINARY_API_KEY is required');
  }
  
  if (!uploadPreset || uploadPreset === 'your_upload_preset' || uploadPreset === 'your_upload_preset_here') {
    throw new Error('VITE_CLOUDINARY_UPLOAD_PRESET is required');
  }
  
  return {
    cloudName,
    apiKey,
    apiSecret,
    uploadPreset
  };
}

/**
 * Validates that all required Cloudinary configuration is present
 */
export function validateCloudinaryConfig(): { isValid: boolean; errors: string[]; config?: CloudinaryConfig } {
  const errors: string[] = [];
  
  try {
    const config = getCloudinaryConfig();
    
    // Additional validation
    if (config.cloudName.length < 3) {
      errors.push('Cloud name must be at least 3 characters long');
    }
    
    if (config.apiKey.length < 10) {
      errors.push('API key appears to be invalid (too short)');
    }
    
    if (!config.uploadPreset) {
      errors.push('Upload preset is required for unsigned uploads');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      config: errors.length === 0 ? config : undefined
    };
    
  } catch (error) {
    errors.push(error instanceof Error ? error.message : 'Unknown configuration error');
    
    return {
      isValid: false,
      errors
    };
  }
}

/**
 * Logs current configuration status (without exposing secrets)
 */
export function logConfigurationStatus(): void {
  console.log('🔧 Cloudinary Configuration Status:');
  
  const envVars = {
    VITE_CLOUDINARY_URL: import.meta.env.VITE_CLOUDINARY_URL,
    VITE_CLOUDINARY_CLOUD_NAME: import.meta.env.VITE_CLOUDINARY_CLOUD_NAME,
    VITE_CLOUDINARY_API_KEY: import.meta.env.VITE_CLOUDINARY_API_KEY,
    VITE_CLOUDINARY_UPLOAD_PRESET: import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET,
  };
  
  Object.entries(envVars).forEach(([key, value]) => {
    const status = value ? '✅ Set' : '❌ Missing';
    const maskedValue = value && value.length > 10 
      ? `${value.substring(0, 6)}...${value.substring(value.length - 4)}`
      : value || 'Not set';
    
    console.log(`  ${key}: ${status} (${maskedValue})`);
  });
  
  const validation = validateCloudinaryConfig();
  console.log(`🔍 Overall Status: ${validation.isValid ? '✅ Valid' : '❌ Invalid'}`);
  
  if (validation.errors.length > 0) {
    console.log('❌ Errors:');
    validation.errors.forEach(error => console.log(`  - ${error}`));
  }
} 